<template>
<div :class="block.class" :id="block.anchor ? block.anchor : ('block-' + block.id)">
    <section class="section">
        <div class="container">
            <h3 class="title is-5">Search results</h3>
            <div class="columns">
                <div class="column is-8">
                    <form class="form" method="post" @submit.prevent="onSearchSubmit">
                        <fieldset :disabled="busy">
                            <div class="field has-addons">
                              <div class="control is-fullwidth">
                                <input :class="['input', {'is-large': !is_small}]" type="text" v-model="term" placeholder="Search term...">
                              </div>
                              <div class="control">
                                <button type="submit" :class="['button is-purple', {'is-large': !is_small}]">
                                  Search
                                </button>
                              </div>
                            </div>
                        </fieldset>
                    </form>
                    <p style="margin-top: .5em;" v-if="results">{{ results.item_count.kmark() }} item{{ results.item_count > 1 ? 's' : '' }} found.</p>
                </div>
            </div>
        </div>
    </section>
    <div class="section bg-purple-lite">
        <div class="container">
            <h2 class="is-sr-only">Search results and filters</h2>
            <div class="columns">
                <div v-if="$route.query.term && tags && tags.length" class="column is-4 order-2">
                    <form class="search-filters" v-if="filters" @submit.prevent="onSearchSubmit">
                        <h3 class="title is-5">Filter by tags</h3>
                        <fieldset :disabled="busy">
                            <template v-if="tags">
                                <p
                                    class="field"
                                    v-for="tag in tags"
                                    :key="`tag-${tag.id}`"
                                >
                                    <label class="checkbox">
                                      <input type="checkbox" :checked="isChecked(tag)" @input="handleTagToggled(tag, $event)">
                                      {{ tag.title }} <span class="button is-small is-rounded">{{ countMe(tag) }}</span> item{{ tag.pages > 1 ? 's' : '' }}
                                    </label>
                                </p>
                            </template>
                        </fieldset>
                    </form>
                </div>
                <div class="column is-8 order-1">
                    <p v-if="!$route.query.term">
                        Please search something first.
                    </p>
                    <div class="loader-holder has-text-centered" v-if="searching">
                        <img src="@/assets/loader.svg" />
                    </div>
                    <div class="search-result" v-if="results && results.results">
                        <p v-if="!results.results.length">
                            no result
                        </p>
                        <template v-else>
                            <SearchResultItem
                                v-for="(item, i) in results.results"
                                :key="`item-${i}`"
                                :item="item"
                            />
                            <Pagination class="bg-purple-lite" :page_count="results.page_count" />
                        </template>
                    </div>
                </div>
            </div>
        </div>
    </div>
</div>
</template>

<script>
import BlockCommons from '../../../mixins/BlockCommons';
import Pagination from '@/components/blocks/Pagination';
import SearchResultItem from './SearchResultItem';
import WindowResizeHandler from '../../../mixins/WindowResizeHandler';

export default {
    name: 'SearchBlock',
    mixins: [ BlockCommons, WindowResizeHandler ],
    components: { SearchResultItem, Pagination },
    data() {
        return {
            searching: false,
            filtering: false,
            is_small: false,
            term: false,
            results: null,
            toggled_tags: []
        }
    },
    computed: {
        busy() {
            return this.searching || this.filtering
        },
        filters() {
            if (!this.block) {
                return null
            }

            return this.block.filters
        },
        tags() {
            if (!this.filters) {
                return null
            }

            return this.filters.tags
        }
    },
    created() {
        this.term = this.$route.query.term ? this.$route.query.term : null

        if (this.$route.query.tags && this.tags) {
            const giventags = JSON.parse(this.$route.query.tags)
            this.toggled_tags = this.tags.filter( o => giventags.includes(o.id))
        }

        if (this.term) {
            this.search()
        }
    },
    watch: {
        $route(to, from) {
            this.search()
        }
    },
    methods: {
        handle_window_resize(width) {
            this.is_small = width < 768;
        },
        countMe(tag) {
            if (this.results && this.results.meta) {
                const found = this.results.meta.find(o => o.id == tag.id)

                if (found) {
                    return found.count
                }
            }

            return 0
        },
        isChecked(tag) {
            return this.toggled_tags.find(o => o.id == tag.id)
        },
        handleTagToggled(tag, e) {
            const found = this.toggled_tags.findIndex(o => o.id == tag.id)

            if (found >= 0 && !e.target.checked) {
                this.toggled_tags.splice(found, 1)
            } else if (found < 0 && e.target.checked) {
                this.toggled_tags.push(tag)
            }

            this.onSearchSubmit()
        },
        onSearchSubmit() {
            let q = {
                term: this.term
            }

            if (this.toggled_tags.length) {
                q.tags = JSON.stringify(this.toggled_tags.map( o => o.id))
            }

            this.$router.push({
                query: q
            }).catch(err => {})
        },
        search() {
            if (this.busy || !this.term) {
                if (!this.term) {
                    this.results = null
                }
                return
            }

            this.searching = true
            this.results = null

            const data = new FormData()
            data.append("term", this.term)
            data.append("page", this.$route.query.page ? parseInt(this.$route.query.page) : 0)
            const tags = this.toggled_tags.map(o => o.id)
            if (tags.length) {
                data.append("tags", tags)
            }

            axios.post(
                base_url + "search" + this.$route.path,
                data
            ).then(resp => {
                this.results = resp.data
                this.searching = false
            })
        }
    }
}
</script>
<style lang="scss" scoped>
#app {
    .search-filters {
        position: sticky;
        top: calc(64px + 1rem);
        padding: .25rem 1rem 1rem;
        background-color: white;
        .title {
            color: #574596;
        }
    }

    .field {
        .control {
            &.is-fullwidth {
                width: 100%;
            }
        }
    }

    label.checkbox {
        * {
            vertical-align: middle;
        }

        input,
        .button {
            transform: translateY(-2px);
        }

        input {
            margin-right: .45em;
        }

        .button {
            &.is-small {
                line-height: 1.2;
                height: auto;
            }
        }
    }
}
</style>



// WEBPACK FOOTER //
// src/components/blocks/modules/search/SearchBlock.vue